import {
  createContext,
  createElement,
  useContext,
  useMemo,
  type FC,
  type PropsWithChildren,
} from 'react';

export interface DIServiceToken<T> {
  readonly _tag: string;
  readonly _type?: T;
}

export function createServiceToken<T>(tag: string): DIServiceToken<T> {
  return { _tag: tag } as DIServiceToken<T>;
}

type ServiceMap = Map<DIServiceToken<unknown>, unknown>;

const DIContext = createContext<ServiceMap | null>(null);

export interface ServiceProviderProps {
  services: [DIServiceToken<unknown>, unknown][];
}

export const ServiceProvider: FC<PropsWithChildren<ServiceProviderProps>> = ({
  services,
  children,
}) => {
  const map = useMemo(() => new Map(services), [services]);
  return createElement(DIContext.Provider, { value: map }, children);
};

export function useService<T>(token: DIServiceToken<T>): T {
  const map = useContext(DIContext);
  if (!map) {
    throw new Error(
      `useService: no ServiceProvider found in the tree. ` +
        `Wrap your app with <ServiceProvider> to use service "${token._tag}".`,
    );
  }
  const instance = map.get(token as DIServiceToken<unknown>) as T | undefined;
  if (instance === undefined) {
    throw new Error(
      `useService: service "${token._tag}" not registered. ` +
        `Add it to the <ServiceProvider services={...}> array.`,
    );
  }
  return instance;
}
